import { Socket } from 'socket.io-client';

// ============================================
// Notification Event Types (adicionar novos aqui)
// ============================================

export const NOTIFICATION_EVENTS = {
  MEET_ENTER_REQUEST: 'meet-enter-request',
  MEET_ENTER_REQUEST_RESOLVED: 'meet-enter-request-resolved',
  MEET_PARTICIPANT_JOINED: 'meet-participant-joined',
  MEET_ENTER_EXTERNAL_USER: 'meet-enter-external-user',
  MEET_ENTER_ALLOWED: 'meet-enter-allowed',
  MEET_ENTER_DENIED: 'meet-enter-denied',
  MEET_CHAT_CREDENTIALS: 'meet-chat-credentials',
  MEET_CHAT_READY: 'meet-chat-ready',
  MEET_UPGRADE: 'meet-upgrade',
  MEET_RAISE_HAND: 'meet-raise-hand',
  MEET_LOWER_HAND: 'meet-lower-hand',
  // Destinatário recusou a chamada de áudio (individual). O caller usa para
  // encerrar o ring imediatamente. Enviado pelo smartspace-fe (SDC-13235).
  MEET_CALL_REJECTED: 'meet-call-rejected',
  MEET_SMARTCOACH_INSIGHT: 'meet-smartcoach-insight',
  MEET_PARTICIPANTS_INVITED: 'meet-participants-invited',
} as const;

export type NotificationEventType =
  | (typeof NOTIFICATION_EVENTS)[keyof typeof NOTIFICATION_EVENTS]
  | string;

// ============================================
// Recipient Types
// ============================================

export interface NotificationRecipient {
  type: 'USER' | 'GROUP' | 'ROOM';
  id: string;
}

// ============================================
// Base Notification Structure (do WebSocket)
// ============================================

export interface BaseNotificationPayload {
  id: string;
  title: string;
  message: string;
  eventName: string;
  type: 'DISPLAY' | 'SILENT' | 'SYSTEM' | 'ACTION';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  senderId: string;
  senderName: string;
  category: 'SYSTEM' | 'USER' | 'ROOM' | 'CHAT';
  recipients: NotificationRecipient[];
  status: 'UNREAD' | 'READ' | 'DISMISSED';
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Specific Notification Payloads
// ============================================

// Payload para meet-enter-request (parsed de message)
export interface MeetEnterRequestMessage {
  roomId: string;
  requesterId: string;
  requesterName: string;
  roomName: string | null;
  message: string;
}

export interface MeetEnterRequestPayload extends BaseNotificationPayload {
  eventName: typeof NOTIFICATION_EVENTS.MEET_ENTER_REQUEST;
  parsedMessage: MeetEnterRequestMessage;
}

// Payload para meet-enter-allowed (quando usuÃ¡rio Ã© aprovado)
export interface MeetEnterAllowedMessage {
  roomId: string;
  roomName?: string | null;
  message?: string;
  allowed: boolean;
  token: string; // Token JWT para conectar na sala
  chatIdentifier?: string;
  xmppUser?: string;
  xmppPw?: string;
  xmppDomain?: string;
  temporaryUserId?: string;
  expiresAt?: string;
}

export interface MeetEnterAllowedPayload extends BaseNotificationPayload {
  eventName: typeof NOTIFICATION_EVENTS.MEET_ENTER_ALLOWED;
  parsedMessage: MeetEnterAllowedMessage;
}

// Payload para meet-enter-denied (quando usuÃ¡rio Ã© rejeitado)
// TODO: Ajustar campos quando a estrutura da mensagem for definida
export interface MeetEnterDeniedMessage {
  roomId: string;
  roomName?: string | null;
  reason?: string;
  message?: string;
}

export interface MeetEnterDeniedPayload extends BaseNotificationPayload {
  eventName: typeof NOTIFICATION_EVENTS.MEET_ENTER_DENIED;
  parsedMessage: MeetEnterDeniedMessage;
}

export interface MeetUpgradePayload extends BaseNotificationPayload {
  eventName: typeof NOTIFICATION_EVENTS.MEET_UPGRADE;
  parsedMessage: MeetUpgradeMessage;
}

export interface MeetUpgradeMessage {
  migrationId?: string;
  sourceRoomId?: string;
  identity?: string;
  chatIdentifier?: string;
  roomId: string;
  roomIdentifier: string;
  roomName: string | null;
  token: string;
  message: string;
}

// Mensagem de recusa da chamada (parsed de message). Enviada pelo destinatário
// ao recusar uma chamada de áudio individual (SDC-13235).
export interface MeetCallRejectedMessage {
  roomId: string;
  rejectedBy?: string;
  callType?: 'audio' | 'video';
  message?: string;
}

export interface MeetRaiseHandMessage {
  roomId: string;
  participantId: string;
  message?: string;
}

export interface MeetRaiseHandPayload extends BaseNotificationPayload {
  eventName: typeof NOTIFICATION_EVENTS.MEET_RAISE_HAND;
  parsedMessage: MeetRaiseHandMessage;
}

export interface MeetLowerHandMessage {
  roomId: string;
  participantId: string;
  message?: string;
}

export interface MeetLowerHandPayload extends BaseNotificationPayload {
  eventName: typeof NOTIFICATION_EVENTS.MEET_LOWER_HAND;
  parsedMessage: MeetLowerHandMessage;
}

export interface MeetParticipantsInvitedMessage {
  roomId: string;
  invitedCount: number;
  inviterName: string;
  message?: string;
}

export interface MeetParticipantsInvitedPayload extends BaseNotificationPayload {
  eventName: typeof NOTIFICATION_EVENTS.MEET_PARTICIPANTS_INVITED;
  parsedMessage: MeetParticipantsInvitedMessage;
}

// Union type para todos os payloads especÃ­ficos
export type SpecificNotificationPayload =
  | MeetEnterRequestPayload
  | MeetEnterAllowedPayload
  | MeetEnterDeniedPayload
  | MeetUpgradePayload
  | MeetRaiseHandPayload
  | MeetLowerHandPayload
  | MeetUpgradePayload
  | MeetSmartCoachInsightPayload
  | MeetParticipantsInvitedPayload;

// ============================================
// Notification Item (processado para uso na UI)
// ============================================

// Union de todos os tipos de mensagem parseada
export type ParsedMessageType =
  | MeetCallRejectedMessage
  | MeetEnterRequestMessage
  | MeetEnterAllowedMessage
  | MeetEnterDeniedMessage
  | MeetUpgradeMessage
  | MeetRaiseHandMessage
  | MeetLowerHandMessage
  | MeetSmartCoachInsightMessage
  | MeetParticipantsInvitedMessage;

// ============================================
// Type Guards (para narrowing seguro)
// ============================================

export function isMeetParticipantsInvitedMessage(
  message: ParsedMessageType | undefined,
): message is MeetParticipantsInvitedMessage {
  return message !== undefined && 'invitedCount' in message;
}

export function isMeetEnterRequestMessage(
  message: ParsedMessageType | undefined,
): message is MeetEnterRequestMessage {
  return message !== undefined && 'requesterId' in message && 'requesterName' in message;
}

export function isMeetEnterAllowedMessage(
  message: ParsedMessageType | undefined,
): message is MeetEnterAllowedMessage {
  return message !== undefined && 'allowed' in message && 'token' in message;
}

export function isMeetEnterDeniedMessage(
  message: ParsedMessageType | undefined,
): message is MeetEnterDeniedMessage {
  return message !== undefined && ('reason' in message || 'message' in message) && !('token' in message);
}

export function isMeetUpgradeMessage(
  message: ParsedMessageType | undefined,
): message is MeetUpgradeMessage {
  return message !== undefined && 'roomIdentifier' in message && 'token' in message;
}

export function isMeetRaiseHandMessage(
  message: ParsedMessageType | undefined,
): message is MeetRaiseHandMessage {
  return (
    message !== undefined &&
    'participantId' in message &&
    !('allowed' in message) &&
    !('requesterId' in message)
  );
}

export function isMeetLowerHandMessage(
  message: ParsedMessageType | undefined,
): message is MeetLowerHandMessage {
  // Como as mensagens de raise e lower podem ser iguais na estrutura (apenas participantId),
  // o ideal Ã© checar pelo eventName no processamento.
  return (
    message !== undefined &&
    'participantId' in message &&
    !('allowed' in message) &&
    !('requesterId' in message)
  );
}

export interface MeetSmartCoachInsightMessage {
  roomId: string;
  insight: string;
}

export interface MeetSmartCoachInsightPayload extends BaseNotificationPayload {
  eventName: typeof NOTIFICATION_EVENTS.MEET_SMARTCOACH_INSIGHT;
  parsedMessage: MeetSmartCoachInsightMessage;
}

export function isMeetSmartCoachInsightMessage(
  message: ParsedMessageType | undefined
): message is MeetSmartCoachInsightMessage {
  return message !== undefined && 'insight' in message;
}

export interface NotificationItem {
  id: string;
  event: NotificationEventType;
  payload: BaseNotificationPayload;
  parsedMessage?: ParsedMessageType;
  timestamp: Date;
  dismissed: boolean;
}

// ============================================
// Legacy types (mantidos para compatibilidade)
// ============================================

export type NotificationData = {
  timestamp?: string | number | Date;
  createdAt?: string | number | Date;
  [key: string]: unknown;
};

// ============================================
// Store State
// ============================================

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

export type NotificationState = {
  // Connection state
  status: ConnectionStatus;
  socket: Socket | null;
  isRegistered: boolean;

  // Notification state
  lastNotification: NotificationItem | null;
  notifications: NotificationItem[];
  unreadCount: number;

  // Actions
  connect: (userId: string) => void;
  subscribeToRoom: (userId: string, roomId: string) => void;
  unsubscribeFromRoom: (userId: string, roomId: string) => void;
  disconnect: () => void;
  dismissNotification: (id: string) => void;
  clearNotifications: () => void;
  addPendingAccessRequests: (
    roomId: string,
    participants: { id: string; name: string; email?: string | null }[],
  ) => number;
};
